import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  Visibility as PreviewIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import Papa from 'papaparse';
import { EmailTemplate, AlertInfo } from '../types';
import { contactApi, getDefaultAccountId } from '../services/api';

interface EnhancedCSVImportProps {
  template: EmailTemplate | null;
  onRecipientsImported: (recipients: any[]) => void;
  showAlert: (message: string, severity: AlertInfo['severity']) => void;
}

interface ContactValidationResult {
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  variables: Record<string, string>;
  isValid: boolean;
  exists: boolean;
  existingContact?: any;
  issues: string[];
  missingVariables: string[];
}

const EnhancedCSVImport: React.FC<EnhancedCSVImportProps> = ({
  template,
  onRecipientsImported,
  showAlert,
}) => {
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [emailColumnIndex, setEmailColumnIndex] = useState<number>(-1);
  const [variableMapping, setVariableMapping] = useState<Record<string, number>>({});
  const [validationResults, setValidationResults] = useState<ContactValidationResult[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [manualDataOpen, setManualDataOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<ContactValidationResult | null>(null);
  
  console.log("Editing contact", editingContact);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      complete: (results) => {
        if (results.errors.length > 0) {
          showAlert('Error parsing CSV file', 'error');
          console.error('CSV parsing errors:', results.errors);
          return;
        }

        const data = results.data as string[][];
        if (data.length < 2) {
          showAlert('CSV file must have at least a header row and one data row', 'error');
          return;
        }

        setCsvData(data);
        setHeaders(data[0]);
        setEmailColumnIndex(-1);
        setVariableMapping({});
        setValidationResults([]);
        
        // Auto-detect email column
        const emailIndex = data[0].findIndex(header => 
          header.toLowerCase().includes('email') || header.toLowerCase().includes('e-mail')
        );
        if (emailIndex !== -1) {
          setEmailColumnIndex(emailIndex);
        }

        // Auto-map template variables
        if (template && template.variables.length > 0) {
          const autoMapping: Record<string, number> = {};
          template.variables.forEach(variable => {
            const headerIndex = data[0].findIndex(header => 
              header.toLowerCase() === variable.toLowerCase() ||
              header.toLowerCase().includes(variable.toLowerCase())
            );
            if (headerIndex !== -1) {
              autoMapping[variable] = headerIndex;
            }
          });
          setVariableMapping(autoMapping);
        }

        showAlert('CSV file uploaded successfully', 'success');
      },
      header: false,
      skipEmptyLines: true
    });

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateContacts = async () => {
    if (emailColumnIndex === -1) {
      showAlert('Please select the email column', 'error');
      return;
    }

    setIsValidating(true);
    
    try {
      // Parse contacts from CSV
      const contacts = csvData.slice(1).map(row => {
        const contact: any = {
          email: row[emailColumnIndex]?.trim(),
          variables: {}
        };

        // Map standard fields
        const firstNameIndex = headers.findIndex(h => h.toLowerCase().includes('first'));
        const lastNameIndex = headers.findIndex(h => h.toLowerCase().includes('last'));
        const companyIndex = headers.findIndex(h => h.toLowerCase().includes('company'));

        if (firstNameIndex !== -1) contact.firstName = row[firstNameIndex]?.trim();
        if (lastNameIndex !== -1) contact.lastName = row[lastNameIndex]?.trim();
        if (companyIndex !== -1) contact.company = row[companyIndex]?.trim();

        // Map template variables
        Object.entries(variableMapping).forEach(([variable, columnIndex]) => {
          if (columnIndex !== -1 && row[columnIndex]) {
            contact.variables[variable] = row[columnIndex].trim();
          }
        });

        return contact;
      }).filter(contact => contact.email); // Filter out rows without email

      // Validate with backend
      const accountId = getDefaultAccountId();
      const response = await contactApi.validate({ accountId, contacts });
      
      // Add template variable validation
      const enhancedResults = response.results.map((result: any) => {
        const missingVariables = template?.variables.filter(variable => 
          !result.variables[variable] || result.variables[variable].trim() === ''
        ) || [];

        return {
          ...result,
          missingVariables,
          issues: [
            ...result.issues,
            ...missingVariables.map(v => `Missing variable: ${v}`)
          ]
        };
      });

      setValidationResults(enhancedResults);
      showAlert(`Validated ${contacts.length} contacts`, 'success');
    } catch (error) {
      console.error('Error validating contacts:', error);
      showAlert('Failed to validate contacts', 'error');
    } finally {
      setIsValidating(false);
    }
  };

  const handleImport = async () => {
    if (validationResults.length === 0) {
      showAlert('Please validate contacts first', 'error');
      return;
    }

    const validContacts = validationResults.filter(result => 
      result.isValid && result.missingVariables.length === 0
    );

    if (validContacts.length === 0) {
      showAlert('No valid contacts to import', 'error');
      return;
    }

    try {
      const accountId = getDefaultAccountId();
      
      // Create contacts in database
      await contactApi.bulkCreate({
        accountId,
        contacts: validContacts,
        updateExisting
      });

      // Convert to recipients format for campaign
      const recipients = validContacts.map(contact => ({
        email: contact.email,
        variables: contact.variables,
        id: `${Date.now()}_${Math.random()}`
      }));

      onRecipientsImported(recipients);
      showAlert(`Successfully imported ${recipients.length} recipients`, 'success');
      
      // Clear data
      setCsvData([]);
      setHeaders([]);
      setEmailColumnIndex(-1);
      setVariableMapping({});
      setValidationResults([]);
    } catch (error) {
      console.error('Error importing contacts:', error);
      showAlert('Failed to import contacts', 'error');
    }
  };

  const downloadSampleCSV = () => {
    const requiredColumns = ['email'];
    const templateVariables = template?.variables || [];
    const optionalColumns = ['firstName', 'lastName', 'company'];

    const headers = [...requiredColumns, ...optionalColumns, ...templateVariables];

    // Generate sample data based on template variables
    const sampleData = [
      [
        'john.doe@example.com',
        'John',
        'Doe',
        'Acme Corporation',
        ...templateVariables.map(variable => {
          switch (variable.toLowerCase()) {
            case 'firstname':
            case 'first_name':
              return 'John';
            case 'lastname':
            case 'last_name':
              return 'Doe';
            case 'company':
            case 'companyname':
              return 'Acme Corporation';
            case 'position':
            case 'title':
              return 'Software Engineer';
            case 'name':
            case 'fullname':
              return 'John Doe';
            case 'city':
              return 'New York';
            case 'country':
              return 'USA';
            case 'product':
              return 'Premium Plan';
            default:
              return `Sample ${variable}`;
          }
        })
      ],
      [
        'jane.smith@techcorp.com',
        'Jane',
        'Smith',
        'Tech Corp',
        ...templateVariables.map(variable => {
          switch (variable.toLowerCase()) {
            case 'firstname':
            case 'first_name':
              return 'Jane';
            case 'lastname':
            case 'last_name':
              return 'Smith';
            case 'company':
            case 'companyname':
              return 'Tech Corp';
            case 'position':
            case 'title':
              return 'Product Manager';
            case 'name':
            case 'fullname':
              return 'Jane Smith';
            case 'city':
              return 'San Francisco';
            case 'country':
              return 'USA';
            case 'product':
              return 'Basic Plan';
            default:
              return `Sample ${variable}`;
          }
        })
      ],
      [
        'mike.johnson@startup.io',
        'Mike',
        'Johnson',
        'Startup Inc',
        ...templateVariables.map(variable => {
          switch (variable.toLowerCase()) {
            case 'firstname':
            case 'first_name':
              return 'Mike';
            case 'lastname':
            case 'last_name':
              return 'Johnson';
            case 'company':
            case 'companyname':
              return 'Startup Inc';
            case 'position':
            case 'title':
              return 'CTO';
            case 'name':
            case 'fullname':
              return 'Mike Johnson';
            case 'city':
              return 'Austin';
            case 'country':
              return 'USA';
            case 'product':
              return 'Enterprise Plan';
            default:
              return `Sample ${variable}`;
          }
        })
      ]
    ];

    const csvContent = [headers, ...sampleData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sample_${template?.name.replace(/[^a-zA-Z0-9]/g, '_') || 'contacts'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getValidationIcon = (result: ContactValidationResult) => {
    if (!result.isValid) return <ErrorIcon color="error" />;
    if (result.missingVariables.length > 0) return <WarningIcon color="warning" />;
    if (result.exists) return <WarningIcon color="info" />;
    return <CheckCircleIcon color="success" />;
  };

  const getValidationColor = (result: ContactValidationResult) => {
    if (!result.isValid) return 'error.light';
    if (result.missingVariables.length > 0) return 'warning.light';
    if (result.exists) return 'info.light';
    return 'success.light';
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Enhanced CSV Import
        {template && (
          <Chip 
            label={`Template: ${template.name}`} 
            size="small" 
            sx={{ ml: 2 }} 
            color="primary"
          />
        )}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Upload CSV File
            </Typography>
            
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              ref={fileInputRef}
            />
            
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ mr: 2 }}
              >
                Upload CSV
              </Button>
              
              <Button
                variant="text"
                startIcon={<DownloadIcon />}
                onClick={downloadSampleCSV}
                size="small"
              >
                Download Template Sample
              </Button>
            </Box>

            {csvData.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Loaded {csvData.length - 1} rows from CSV file
              </Alert>
            )}

            {template && template.variables.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Required template variables:
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {template.variables.map(variable => (
                    <Chip key={variable} label={variable} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                  ))}
                </Box>
              </Alert>
            )}
          </Paper>
        </Grid>

        {csvData.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Column Mapping
              </Typography>

              {/* Email Column Selection */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Email Column</InputLabel>
                <Select
                  value={emailColumnIndex}
                  onChange={(e) => setEmailColumnIndex(e.target.value as number)}
                  label="Email Column"
                >
                  <MenuItem value={-1}>
                    <em>Select email column</em>
                  </MenuItem>
                  {headers.map((header, index) => (
                    <MenuItem key={index} value={index}>
                      {header} (Column {index + 1})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Template Variable Mapping */}
              {template && template.variables.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Map Template Variables
                  </Typography>
                  {template.variables.map((variable) => (
                    <FormControl fullWidth key={variable} sx={{ mb: 1 }}>
                      <InputLabel>{variable}</InputLabel>
                      <Select
                        value={variableMapping[variable] || -1}
                        onChange={(e) => setVariableMapping(prev => ({
                          ...prev,
                          [variable]: e.target.value as number
                        }))}
                        label={variable}
                      >
                        <MenuItem value={-1}>
                          <em>Select column for {variable}</em>
                        </MenuItem>
                        {headers.map((header, index) => (
                          <MenuItem key={index} value={index}>
                            {header} (Column {index + 1})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ))}
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={validateContacts}
                  disabled={emailColumnIndex === -1 || isValidating}
                  startIcon={isValidating ? <LinearProgress sx={{ width: 20 }} /> : <CheckCircleIcon />}
                >
                  {isValidating ? 'Validating...' : 'Validate Contacts'}
                </Button>

                {validationResults.length > 0 && (
                  <Button
                    variant="outlined"
                    startIcon={<PreviewIcon />}
                    onClick={() => setPreviewOpen(true)}
                  >
                    Preview Results
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>
        )}

        {validationResults.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1">
                  Validation Results
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={updateExisting}
                      onChange={(e) => setUpdateExisting(e.target.checked)}
                    />
                  }
                  label="Update existing contacts"
                />
              </Box>

              <Alert
                severity={validationResults.some(r => !r.isValid || r.missingVariables.length > 0) ? 'warning' : 'success'}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2">
                  <strong>{validationResults.length}</strong> contacts processed •
                  <strong> {validationResults.filter(r => r.isValid && r.missingVariables.length === 0).length}</strong> ready to import •
                  <strong> {validationResults.filter(r => r.exists).length}</strong> already exist •
                  <strong> {validationResults.filter(r => !r.isValid || r.missingVariables.length > 0).length}</strong> need attention
                </Typography>
              </Alert>

              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Variables</TableCell>
                      <TableCell>Issues</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validationResults.map((result, index) => (
                      <TableRow
                        key={index}
                        sx={{ backgroundColor: getValidationColor(result) }}
                      >
                        <TableCell>
                          <Tooltip title={result.issues.join(', ') || 'Valid'}>
                            {getValidationIcon(result)}
                          </Tooltip>
                        </TableCell>
                        <TableCell>{result.email}</TableCell>
                        <TableCell>
                          {[result.firstName, result.lastName].filter(Boolean).join(' ') || '-'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {Object.entries(result.variables).map(([key, value]) => (
                              <Chip
                                key={key}
                                label={`${key}: ${value}`}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                            {result.missingVariables.map(variable => (
                              <Chip
                                key={variable}
                                label={`${variable}: missing`}
                                size="small"
                                color="error"
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {result.issues.length > 0 ? (
                            <Box>
                              {result.issues.map((issue, i) => (
                                <Chip key={i} label={issue} size="small" color="error" sx={{ mr: 0.5, mb: 0.5 }} />
                              ))}
                            </Box>
                          ) : (
                            <Chip label="Valid" size="small" color="success" />
                          )}
                        </TableCell>
                        <TableCell>
                          {result.missingVariables.length > 0 && (
                            <IconButton
                              size="small"
                              onClick={() => setEditingContact(result)}
                              color="primary"
                            >
                              <AddIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* {validationResults.length > 10 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="text.secondary">
                            ... and {validationResults.length - 10} more contacts
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )} */}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleImport}
                  disabled={validationResults.filter(r => r.isValid && r.missingVariables.length === 0).length === 0}
                  color="success"
                >
                  Import {validationResults.filter(r => r.isValid && r.missingVariables.length === 0).length} Contacts
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => setManualDataOpen(true)}
                  disabled={validationResults.filter(r => r.missingVariables.length > 0).length === 0}
                >
                  Fix Missing Data ({validationResults.filter(r => r.missingVariables.length > 0).length})
                </Button>
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Validation Preview</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Showing first 5 contacts from validation results
          </Typography>
          {/* Preview content would go here */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Manual Data Entry Dialog */}
      <Dialog open={manualDataOpen} onClose={() => setManualDataOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Fix Missing Template Variables</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Add missing template variables for contacts that need them
          </Typography>
          {/* Manual data entry form would go here */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualDataOpen(false)}>Cancel</Button>
          <Button variant="contained">Save Changes</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EnhancedCSVImport;
